<template>
  <q-form ref="form" greedy no-error-focus>
    <!-- Disabling is applied through the injected formDisabled state (see useFieldsetState), not a
    native fieldset[disabled], so individual fields/buttons can opt out via form-state-inheritance-disabled. -->
    <fieldset :class="fieldsetClass">
      <slot />
    </fieldset>
  </q-form>
</template>

<script setup>
import { ref, provide, computed } from "vue"

const props = defineProps({
  disable: Boolean,
  readonly: Boolean,
  fieldsetClass: String
})

provide(
  "formDisabled",
  computed(() => props.disable)
)
provide(
  "formReadonly",
  computed(() => props.readonly)
)

const form = ref(null)

// Station forms are validated as clones inside a hidden container (see AppFormsValidator), so a failing
// rule renders its message where nobody can see it. Name the offending fields on the console instead.
const reportInvalidFields = () => {
  const invalid = form.value
    .getValidationComponents()
    .filter((component) => component.hasError)
    .map((component) => ({
      label: component.$props.label || component.$props.name || "(unlabelled)",
      value: component.$props.modelValue,
      error: component.$el?.innerText?.trim()
    }))

  if (invalid.length) {
    // eslint-disable-next-line no-console -- a diagnostic, not an application error
    console.warn("[AppForm] validation failed for:", invalid)
  }
}

defineExpose({
  // No short-circuit on disable/readonly: locked fields (and locked AppFormRules) already yield no
  // validation rules, so QForm.validate() naturally validates only the reachable fields - those that
  // opt out of the lock via form-state-inheritance-disabled or always-validate (e.g. REN-editable or
  // calculated fields). Short-circuiting here would wrongly skip those.
  validate: async () => {
    const valid = await form.value.validate()

    if (!valid) {
      reportInvalidFields()
    }

    return valid
  },
  resetValidation: () => {
    return form.value.resetValidation()
  }
})
</script>

<style lang="scss" scoped>
fieldset {
  border: none;
  padding: 0;
  // Fieldset elements default to min-width: min-content, which prevents them
  // from shrinking below their content width inside flex layouts. This causes
  // child elements (e.g. selects with long text) to overflow the flex column.
  min-width: 0;

  &[disabled] {
    // Opacity will be set for children elements individually
    opacity: 1 !important;
  }
}
</style>
